import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { debug } from "../../../utils/debugLogger";
import {
  canonicalHexColor,
  HEX_COLOR_ERROR,
} from "../utils/brandFormValidation";

interface ColorItem {
  value: string;
  type: "primary" | "secondary" | "accent" | "other";
}

interface BrandColorPickerProps {
  colors: ColorItem[];
  onColorsChange: (colors: ColorItem[]) => void;
  maxColors?: number;
}

const colorTypes: ColorItem["type"][] = [
  "primary",
  "secondary",
  "accent",
  "other",
];

export const BrandColorPicker: React.FC<BrandColorPickerProps> = ({
  colors,
  onColorsChange,
  maxColors = 4,
}) => {
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  // What is in the box, verbatim, while a row is being typed into. The saved
  // colour and the keystrokes are two different things (SGL-342): normalising
  // every keystroke would rewrite "648" into #664488 under the customer's cursor,
  // so the draft is what they see and the canonical value is what we keep.
  const [drafts, setDrafts] = useState<Record<number, string>>({});
  const [blurErrors, setBlurErrors] = useState<Record<number, boolean>>({});

  const commitColor = (index: number, value: string) => {
    const newColors = [...colors];
    newColors[index] = { ...newColors[index], value };
    onColorsChange(newColors);
  };

  const clearRowState = (index: number) => {
    setDrafts((prev) => {
      const { [index]: _draft, ...rest } = prev;
      return rest;
    });
    setBlurErrors((prev) => {
      const { [index]: _error, ...rest } = prev;
      return rest;
    });
  };

  /** The native swatch always hands us a valid lowercase hex. */
  const handleSwatchChange = (index: number, value: string) => {
    clearRowState(index);
    commitColor(index, canonicalHexColor(value) ?? value);
  };

  const handleHexTyping = (index: number, raw: string) => {
    setDrafts((prev) => ({ ...prev, [index]: raw }));
    setBlurErrors((prev) => ({ ...prev, [index]: false }));
    // Six hex digits is a finished colour, so apply it the moment it is there —
    // that is what makes typing `648B7F` with no hash move the swatch and the
    // preview straight away. Three digits is ambiguous mid-word, so the shorthand
    // waits for blur.
    const typed = raw.trim().replace(/^#/, "");
    if (typed.length === 6) {
      const canonical = canonicalHexColor(raw);
      if (canonical) commitColor(index, canonical);
    }
  };

  const handleHexBlur = (index: number) => {
    const draft = drafts[index];
    if (draft === undefined) return;
    // An empty box is not an attempt at a bad colour — someone cleared it to
    // retype. Put the colour back; removing a colour is what the delete button is
    // for (SGL-224 made a row removable down to zero).
    if (draft.trim() === "") {
      clearRowState(index);
      return;
    }
    const canonical = canonicalHexColor(draft);
    if (!canonical) {
      // Keep the draft on screen so it can be corrected, and commit nothing — an
      // unusable colour must not reach the saved brand record.
      setBlurErrors((prev) => ({ ...prev, [index]: true }));
      return;
    }
    clearRowState(index);
    commitColor(index, canonical);
  };

  const handleTypeChange = (index: number, type: ColorItem["type"]) => {
    const newColors = [...colors];
    newColors[index] = { ...newColors[index], type };
    onColorsChange(newColors);
  };

  const handleAddColor = () => {
    if (colors.length < maxColors) {
      const usedTypes = colors.map((c) => c.type);
      const availableType =
        colorTypes.find((t) => !usedTypes.includes(t)) || "other";
      // A new row opens on neutral black, never on a colour from another brand's
      // record (SGL-224). The customer picks from here; we do not pre-decide.
      onColorsChange([...colors, { value: "#000000", type: availableType }]);
    }
  };

  const handleRemoveColor = (index: number) => {
    // Drafts and errors are keyed by position, so a removal has to shift them the
    // same way the list shifts — otherwise the row below inherits the error of the
    // row that was just deleted.
    const shiftDown = <T,>(map: Record<number, T>): Record<number, T> =>
      Object.entries(map).reduce<Record<number, T>>((acc, [key, value]) => {
        const at = Number(key);
        if (at === index) return acc;
        acc[at > index ? at - 1 : at] = value;
        return acc;
      }, {});
    setDrafts(shiftDown);
    setBlurErrors(shiftDown);
    // Removable down to zero (SGL-224): a customer who was given a wrong palette
    // must be able to clear it entirely, not be floored at one colour they never chose.
    onColorsChange(colors.filter((_, i) => i !== index));
  };

  const handleCopyHex = async (hex: string, index: number) => {
    try {
      await navigator.clipboard.writeText(hex);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      debug.error("Failed to copy:", err);
    }
  };

  return (
    <motion.div
      initial={{ y: 10, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 0.3 }}
      className="space-y-3"
    >
      <div className="flex items-center justify-between">
        <motion.label
          className="block text-sm font-medium text-[#2f4941]"
          whileHover={{ scale: 1.01 }}
        >
          Brand Colors
        </motion.label>
        <span className="text-xs text-gray-500">
          {colors.length} / {maxColors} colors
        </span>
      </div>

      <div className="space-y-2">
        <AnimatePresence mode="popLayout">
          {colors.map((color, index) => {
            const canonical = canonicalHexColor(color.value);
            // A row is in error either because what was just typed is not a
            // colour, or because the brand was saved with an unusable value
            // before SGL-342 — the customer should be told either way instead of
            // being left to work out why the swatch never moved.
            const rowError =
              blurErrors[index] ||
              (drafts[index] === undefined && canonical === null)
                ? HEX_COLOR_ERROR
                : null;
            const displayValue = drafts[index] ?? color.value;
            const swatchValue = (canonical ?? "#000000").toLowerCase();
            const errorId = `brand-color-error-${index}`;
            return (
              <motion.div
                key={index}
                initial={{ scale: 0.9, opacity: 0, x: -20 }}
                animate={{ scale: 1, opacity: 1, x: 0 }}
                exit={{ scale: 0.9, opacity: 0, x: 20 }}
                transition={{ duration: 0.2 }}
                className="flex flex-col gap-1 p-2 bg-gray-50 rounded-lg border border-gray-200"
              >
                <div className="flex items-center gap-2">
                  {/* Color Preview. Fed the canonical value in lowercase: the native
                      colour input accepts only lowercase `#rrggbb`, and a brand saved
                      before SGL-342 can still hold a value it cannot render at all,
                      which shows as black rather than as a broken control. */}
                  <div className="relative">
                    <input
                      type="color"
                      value={swatchValue}
                      onChange={(e) => handleSwatchChange(index, e.target.value)}
                      className="w-12 h-12 rounded border-2 border-gray-300 cursor-pointer"
                      style={{ backgroundColor: swatchValue }}
                      aria-label={`${color.type} color swatch`}
                    />
                  </div>

                  {/* Hex Input. The placeholder is a neutral hint, not a brand colour
                      (SGL-224) — it was #2f4941, Sagelyn's accent green, which showed
                      every customer our colour as the suggestion for their own. */}
                  <input
                    type="text"
                    value={displayValue}
                    onChange={(e) => handleHexTyping(index, e.target.value)}
                    onBlur={() => handleHexBlur(index)}
                    placeholder="#000000"
                    aria-label={`${color.type} color hex`}
                    aria-invalid={Boolean(rowError)}
                    aria-describedby={rowError ? errorId : undefined}
                    className={`flex-1 px-3 py-2 text-sm border rounded focus:outline-none focus:ring-2 font-mono ${
                      rowError
                        ? "border-red-500 focus:ring-red-500/30 focus:border-red-500"
                        : "border-gray-300 focus:ring-[#2f4941]/30 focus:border-[#2f4941]"
                    }`}
                    /* Room for a hash, six digits, and the spaces that come along
                       when a value is pasted out of a brand guide. */
                    maxLength={9}
                  />

                  {/* Copy Button. Hands over the canonical form: a brand stored
                      before SGL-342 holds `648B7F`, and copying that is how the
                      same unusable value gets pasted somewhere else. */}
                  <motion.button
                    type="button"
                    onClick={() => handleCopyHex(canonical ?? color.value, index)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="p-2 text-[#2f4941] hover:bg-[#2f4941]/10 rounded transition-colors"
                    title="Copy hex code"
                  >
                    <span className="material-icons text-sm">
                      {copiedIndex === index ? "check" : "content_copy"}
                    </span>
                  </motion.button>

                  {/* Type Dropdown */}
                  <select
                    value={color.type}
                    onChange={(e) =>
                      handleTypeChange(index, e.target.value as ColorItem["type"])
                    }
                    className="px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#2f4941]/30 focus:border-[#2f4941] bg-white capitalize"
                  >
                    {colorTypes.map((type) => (
                      <option key={type} value={type} className="capitalize">
                        {type}
                      </option>
                    ))}
                  </select>

                  {/* Remove Button */}
                  <motion.button
                    type="button"
                    onClick={() => handleRemoveColor(index)}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                    title="Remove color"
                  >
                    <span className="material-icons text-lg">delete</span>
                  </motion.button>
                </div>

                {rowError && (
                  <p id={errorId} className="px-1 text-xs text-red-600" role="alert">
                    {rowError}
                  </p>
                )}
              </motion.div>
            );
          })}
        </AnimatePresence>

        {/* An empty palette is a real state, not a broken one (SGL-224). Say so,
            rather than rendering nothing or filling the slot with someone else's colour. */}
        {colors.length === 0 && (
          <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-center">
            <p className="text-sm text-gray-600">No brand colors set yet.</p>
            <p className="mt-1 text-xs text-gray-500">
              Add the colors from your own branding — we won&apos;t guess them for you.
            </p>
          </div>
        )}
      </div>

      {/* Add Color Button */}
      {colors.length < maxColors && (
        <motion.button
          type="button"
          onClick={handleAddColor}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="w-full py-2 px-4 border-2 border-dashed border-[#2f4941] text-[#2f4941] rounded-lg hover:bg-[#2f4941]/5 transition-colors flex items-center justify-center gap-2 text-sm font-medium"
        >
          <span className="material-icons text-lg">add_circle</span>
          Add Color
        </motion.button>
      )}

      <p className="text-xs text-gray-500 mt-2">
        Choose up to {maxColors} colors that represent your brand. These will be
        used in your social media content.
      </p>
    </motion.div>
  );
};
